import { useUserStore } from '@store/userStore';
import React from 'react';
// src/shell/Routes.jsx
// All lazy-loaded page imports and route declarations live here.
// App.jsx imports this single component — adding a route means editing only this file.

import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

// ── Lazy page imports (Vite splits each into its own chunk) ────────
// feed
const NeighborhoodPage  = lazy(() => import('@features/neighborhood/NeighborhoodPage'));
const FeedPage          = lazy(() => import('@features/feed/FeedPage'));
// alerts / news
const NewsPage          = lazy(() => import('@features/alerts/NewsPage'));
const AlertsSettingsPage = lazy(() => import('@features/alerts/AlertsPage'));
// maps
const MapPage           = lazy(() => import('@features/maps/MapPage'));
const CommandPage       = lazy(() => import('@features/command/CommandPage'));
// weather
const WeatherPage       = lazy(() => import('@features/weather/WeatherPage'));
// hotspots / connectivity
const HotspotsPage      = lazy(() => import('@features/connectivity/HotspotsPage'));
// legal
const LegalPage         = lazy(() => import('@features/legal/LegalPage'));

// SEO pages
const AboutPage       = lazy(() => import('@features/seo/AboutPage'));
const HowItWorksPage  = lazy(() => import('@features/seo/HowItWorksPage'));
const DataSourcesPage = lazy(() => import('@features/seo/DataSourcesPage'));
// guides
const GoBagChecklist    = lazy(() => import('@features/guides/GoBagChecklist'));
const SheltersPage         = lazy(() => import('@features/guides/SheltersPage'));
const ReadinessPage        = lazy(() => import('@features/readiness/ReadinessPage'));
const ResourcesPage        = lazy(() => import('@features/resources/ResourcesPage'));
const QRSharePage          = lazy(() => import('@features/share/QRSharePage'));
const EmergencyBinder      = lazy(() => import('@features/binder/EmergencyBinder'));
const AccessibilityPage    = lazy(() => import('@features/more/AccessibilityPage'));
import OfflineSOSPageComponent from '@features/guides/OfflineSOSPage';
const DamageReportPage  = lazy(() => import('@features/damage/DamageReportPage'));
const OrgPage           = lazy(() => import('@features/org/OrgPage'));
const RosterPage        = lazy(() => import('@features/org/RosterPage'));
const RollCallPage      = lazy(() => import('@features/org/RollCallPage'));
const ResourceRequestPage = lazy(() => import('@features/org/ResourceRequestPage'));
const OfflineSOSPage = OfflineSOSPageComponent;
const DownloadMapPage      = lazy(() => import('@features/maps/DownloadMapPage'));
// admin
const AdminPage         = lazy(() => import('@features/admin/AdminPage'));
// gps
const GPSPage               = lazy(() => import('@features/gps/GPSPage'));
const EmergencyToolsPage    = lazy(() => import('@features/tools/EmergencyToolsPage'));
// evacuation
const EvacPage          = lazy(() => import('@features/evacuation/EvacPage'));
const TrafficPage       = lazy(() => import('@features/evacuation/TrafficPage'));
// radio
const RadioPage         = lazy(() => import('@features/radio/RadioPage'));
// gas
const GasPage           = lazy(() => import('@features/gas/GasPage'));
// guides / first aid / emergency plan
const GuidesPage        = lazy(() => import('@features/guides/GuidesPage'));
const FirstAidPage      = lazy(() => import('@features/guides/FirstAidPage'));
const EmergencyPlanPage = lazy(() => import('@features/guides/EmergencyPlanPage'));
// contacts / hospitals
const ContactsPage      = lazy(() => import('@features/contacts/ContactsPage'));
const HospitalsPage     = lazy(() => import('@features/contacts/HospitalsPage'));
// checklist
const ChecklistPage     = lazy(() => import('@features/checklist/ChecklistPage'));
// family
const CheckInPage       = lazy(() => import('@features/family/CheckInPage'));
const SafeToTravelPage  = lazy(() => import('@features/travel/SafeToTravelPage'));
const TravelPage         = lazy(() => import('@features/travel/TravelPage'));
// missing
const MissingPage       = lazy(() => import('@features/missing/MissingPage'));
// medical
const MedicalIDPage     = lazy(() => import('@features/medical/MedicalIDPage'));
// toolkit
const ToolkitPage       = lazy(() => import('@features/more/ToolkitPage'));
// more / settings
const AccuracyPage = lazy(() => import('@features/legal/AccuracyPage'));
const MorePage          = lazy(() => import('@features/more/MorePage'));
// splash
const SplashPage            = lazy(() => import('@features/splash/SplashPage'));

// ── Loading fallback ──────────────────────────────────────────────
function PageLoader() {
  return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0ebe2' }}>
      <div style={{ fontSize: 32, animation: 'pulse 1.5s ease-in-out infinite' }}>🏠</div>
    </div>
  );
}

// ── AppRoutes ─────────────────────────────────────────────────────
// ── AdminRoute guard ──────────────────────────────────────────────
// Checks Firebase token claims before rendering the admin panel.
// Redirects non-admin users to /feed without loading the JS chunk.
// Short privileged session: admin token validated every 30 minutes.
// Inactivity timeout: 60 minutes of no interaction forces re-auth.
const ADMIN_SESSION_MS   = 30 * 60_000; // re-validate token every 30 min
const ADMIN_INACTIVITY_MS = 60 * 60_000; // expire session after 60 min inactivity

function AdminRoute({ children }) {
  const [status, setStatus]       = React.useState('loading');
  const lastActivityRef           = React.useRef(Date.now());
  const sessionTimerRef           = React.useRef(null);
  const inactivityTimerRef        = React.useRef(null);

  const validateToken = React.useCallback(async () => {
    try {
      const { initFirebase } = await import('@services/storage/firebase');
      const { auth } = await initFirebase();
      const user = auth.currentUser;
      if (!user) { setStatus('denied'); return; }
      const token = await user.getIdTokenResult(true);
      if (token.claims?.admin !== true) { setStatus('denied'); return; }
      setStatus('admin');
    } catch(e) {
      console.warn('[AdminRoute] token validation error:', e.message);
      setStatus('denied');
    }
  }, []);

  const resetInactivity = React.useCallback(() => {
    lastActivityRef.current = Date.now();
    clearTimeout(inactivityTimerRef.current);
    inactivityTimerRef.current = setTimeout(() => {
      console.warn('[AdminRoute] Session expired due to inactivity');
      setStatus('denied');
    }, ADMIN_INACTIVITY_MS);
  }, []);

  React.useEffect(() => {
    // Initial token check
    (async () => {
      try {
        const { initFirebase } = await import('@services/storage/firebase');
        const { auth } = await initFirebase();
        const { onAuthStateChanged } = await import('firebase/auth');
        const unsub = onAuthStateChanged(auth, async (user) => {
          unsub();
          if (!user) { setStatus('denied'); return; }
          const token = await user.getIdTokenResult(true);
          if (token.claims?.admin !== true) { setStatus('denied'); return; }
          setStatus('admin');
          resetInactivity();
          // Periodic token re-validation
          sessionTimerRef.current = setInterval(validateToken, ADMIN_SESSION_MS);
        });
      } catch(e) {
        console.warn('[AdminRoute] error:', e.message);
        setStatus('denied');
      }
    })();

    // Track activity
    const onActivity = () => resetInactivity();
    window.addEventListener('mousemove', onActivity, { passive: true });
    window.addEventListener('keydown',   onActivity, { passive: true });
    window.addEventListener('click',     onActivity, { passive: true });
    window.addEventListener('touchstart',onActivity, { passive: true });

    return () => {
      clearInterval(sessionTimerRef.current);
      clearTimeout(inactivityTimerRef.current);
      window.removeEventListener('mousemove',  onActivity);
      window.removeEventListener('keydown',    onActivity);
      window.removeEventListener('click',      onActivity);
      window.removeEventListener('touchstart', onActivity);
    };
  }, [validateToken, resetInactivity]);

  if (status === 'loading') {
    return (
      <div style={{ display:'flex', alignItems:'center', justifyContent:'center',
                    height:'100dvh', color:'#4b5563', fontSize:12, fontFamily:'system-ui' }}>
        Checking access…
      </div>
    );
  }
  if (status === 'denied') return <Navigate to="/feed" replace />;
  return children;
}

const TomorrowEvalDashboard = lazy(() => import('../ops/evaluation/TomorrowEvalDashboard'));
const OpsDashboard = React.lazy(() => import('../ops/telemetry/OpsDashboard').then(m => ({ default: m.OpsDashboard })));

export function AppRoutes({ onShareOpen }) {
  const zones = useUserStore(s => s.zones);
  const zonesKey = zones.join(',');
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/about"         element={<Suspense fallback={<PageLoader />}><AboutPage /></Suspense>} />
        <Route path="/how-it-works"   element={<Suspense fallback={<PageLoader />}><HowItWorksPage /></Suspense>} />
        <Route path="/data-sources"   element={<Suspense fallback={<PageLoader />}><DataSourcesPage /></Suspense>} />
        <Route path="/"             element={<Navigate to="/feed" replace />} />

        {/* Dashboard */}
        <Route path="/neighborhood" element={<NeighborhoodPage />} />
        <Route path="/feed"         element={<FeedPage />} />
        <Route path="/news"         element={<NewsPage />} />
        <Route path="/missing"      element={<MissingPage />} />
        <Route path="/checklist"    element={<ChecklistPage />} />
        <Route path="/checkin"      element={<CheckInPage />} />
        <Route path="/travel"       element={<TravelPage />} />
        <Route path="/safe-to-travel" element={<SafeToTravelPage />} />

        {/* Resources */}
        <Route path="/gas" key={zonesKey + "/gas"}          element={<GasPage />} />
        <Route path="/contacts"     element={<ContactsPage />} />
        <Route path="/hospitals"    element={<HospitalsPage />} />
        <Route path="/radio" key={zonesKey + "/radio"}        element={<RadioPage />} />
        <Route path="/guides"       element={<GuidesPage />} />
        <Route path="/firstaid"     element={<FirstAidPage />} />
        <Route path="/plan"         element={<EmergencyPlanPage />} />

        {/* Command */}
        <Route path="/command"     element={<CommandPage />} />
        <Route path="/map"          element={<MapPage />} />
        <Route path="/evac" key={zonesKey + "/evac"}         element={<EvacPage />} />
        <Route path="/traffic"      element={<TrafficPage />} />
        <Route path="/hotspots"  element={<HotspotsPage />} />
        <Route path="/accuracy" element={<Suspense fallback={<PageLoader />}><AccuracyPage /></Suspense>} />
          <Route path="/legal"     element={<LegalPage />} />
        <Route path="/gobag"     element={<GoBagChecklist />} />
        <Route path="/accessibility" element={<AccessibilityPage />} />
        <Route path="/binder" key={zonesKey + "/binder"}        element={<EmergencyBinder />} />
        <Route path="/share"         element={<QRSharePage />} />
        <Route path="/resources" key={zonesKey + "/resources"}     element={<ResourcesPage />} />
        <Route path="/readiness"     element={<ReadinessPage />} />
        <Route path="/shelters" key={zonesKey + "/shelters"}      element={<SheltersPage />} />
        <Route path="/sos-offline"   element={<OfflineSOSPage />} />
        <Route path="/download-maps" element={<DownloadMapPage />} />
        <Route path="/admin"     element={<AdminRoute><AdminPage /></AdminRoute>} />
        <Route path="/weather"      element={<WeatherPage />} />
        <Route path="/gps"          element={<GPSPage />} />
        <Route path="/tools"        element={<EmergencyToolsPage />} />

        {/* Tools / settings */}
        <Route path="/medical"      element={<MedicalIDPage />} />
        <Route path="/alerts"       element={<AlertsSettingsPage />} />
        <Route path="/toolkit"      element={<ToolkitPage />} />
        <Route path="/pumps" element={<React.Suspense fallback={null}>{React.createElement(React.lazy(() => import('@features/more/PumpStationMap')))}</React.Suspense>} />
        <Route path="/more"         element={<MorePage onShare={onShareOpen} />} />

        {/* Org & Damage */}
        <Route path='/damage-report' element={<DamageReportPage />} />
        <Route path='/org' element={<OrgPage />} />
        <Route path='/org/roster' element={<RosterPage />} />
        <Route path='/org/rollcall' element={<RollCallPage />} />
        <Route path='/org/resources' element={<ResourceRequestPage />} />

        {/* Catch-all */}
        <Route path='*' element={<Navigate to='/feed' replace />} />
        <Route path="/ops/tomorrow-eval" element={<TomorrowEvalDashboard />} />
        <Route path="/ops/dashboard" element={
            <React.Suspense fallback={<div style={{padding:20,color:'#60a5fa',fontFamily:'monospace'}}>Loading ops dashboard...</div>}>
              <OpsDashboard />
            </React.Suspense>
          } />
        </Routes>
    </Suspense>
  );
}

// ── Splash (rendered before zone is set — outside shell) ──────────
export function SplashRoute({ onComplete }) {
  return (
    <Suspense fallback={<PageLoader />}>
      <SplashPage onComplete={onComplete} />
    </Suspense>
  );
}
